'use client';

/**
 * Widget agent-ensure confirm — the consent chrome behind
 * `host.agents.ensure()`. Twin of {@link WidgetAgentGrant}: portal chrome
 * rendered OVER the canvas, never inside the widget iframe. An app cannot
 * silently create an agent — the owner confirms here, and may review/edit the
 * standing task (acceptance criteria) before the agent is minted.
 *
 * Opened via a window CustomEvent('widget-agent-ensure') whose detail carries
 * { role, task?, reason?, widgetTitle, resolve }. The bridge awaits
 * `resolve({ task })` on confirm, or `resolve(null)` on cancel/backdrop.
 */

import { useEffect, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { ShieldCheck } from 'lucide-react';
import { PopoverPortal } from '@/components/ui/popover-portal';
import { useAppFrameFocusReturn } from '@/components/workspace/app-frame-focus-return';
import { cn } from '@/lib/utils';

interface EnsureRequest {
  role: string;
  task?: string;
  reason?: string;
  widgetTitle: string;
  resolve: (result: { task?: string } | null) => void;
}

const ROLE_LABELS: Record<string, string> = {
  'deal-desk': 'Deal Desk',
};

const ROLE_BLURB: Record<string, string> = {
  'deal-desk':
    'accepts inbound deals from other agents that match your criteria and adds matching ones to your pipeline. It never spends money or sends messages without you.',
};

export function WidgetAgentEnsure() {
  const t = useT();
  const [req, setReq] = useState<EnsureRequest | null>(null);
  // Close hands focus back to the app that asked (app-frame-focus-return.ts).
  const captureAppFocus = useAppFrameFocusReturn(!!req);
  const [task, setTask] = useState('');

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<EnsureRequest>).detail;
      if (!detail || typeof detail.resolve !== 'function') return;
      captureAppFocus();
      setTask(detail.task ?? '');
      setReq(detail);
    };
    window.addEventListener('widget-agent-ensure', onOpen);
    return () => window.removeEventListener('widget-agent-ensure', onOpen);
  }, [captureAppFocus]);

  if (!req) return null;

  const finish = (result: { task?: string } | null) => {
    req.resolve(result);
    setReq(null);
  };

  const roleLabel = ROLE_LABELS[req.role] ?? 'agent';
  const blurb = ROLE_BLURB[req.role] ?? 'runs a standing task for this project on your behalf.';

  return (
    <PopoverPortal
      role="dialog"
      ariaLabel={t('widgetAgent.createProjectAgent')}
      // The panel's place comes from the classes below (a bottom sheet on a
      // phone, a card near the top on a desktop). The alignment was inline
      // too, and an inline style beats `md:items-start`, so on a desktop the
      // dialog sat at the bottom of the page (components/sharing/
      // share-dialog.tsx has the finding, 2026-10-02).
      style={{ position: 'fixed', inset: 0, zIndex: 240, display: 'flex', justifyContent: 'center' }}
      className="items-end bg-black/40 backdrop-blur-sm md:items-start md:py-[14vh]"
    >
      <div aria-hidden className="absolute inset-0" onClick={() => finish(null)} />
      <div
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'glass-panel relative flex w-full flex-col overflow-hidden shadow-2xl',
          'max-h-[90vh] rounded-t-2xl',
          'md:max-h-[70vh] md:w-[min(460px,96vw)] md:rounded-2xl',
        )}
      >
        <div className="shrink-0 px-4 py-3 shadow-[inset_0_-1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_-1px_0_rgba(0,0,0,0.35)]">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <ShieldCheck className="h-3 w-3" /> {t('widgetAgent.createProjectAgent')}
          </div>
          <div className="truncate text-sm font-semibold tracking-tight">
            {req.widgetTitle} wants to create a {roleLabel} for this project
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            The {roleLabel} {blurb}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          <label className="mb-1 block text-xs font-medium text-foreground/80">{t('widgetAgent.acceptanceCriteria')}</label>
          <textarea
            value={task}
            onChange={(e) => setTask(e.target.value)}
            rows={5}
            placeholder={t('widgetAgent.criteriaPlaceholder')}
            className="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <p className="mt-1 text-[11px] text-muted-foreground/70">
            The agent only accepts inbound deals matching this. You can change it later; you approve everything before it acts.
          </p>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 px-4 py-3 shadow-[inset_0_1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_1px_0_rgba(0,0,0,0.35)]">
          <button
            type="button"
            onClick={() => finish(null)}
            className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => finish({ task: task.trim() || undefined })}
            className="rounded-lg bg-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground"
          >
            {t('widgetAgent.createAgent')}
          </button>
        </div>
      </div>
    </PopoverPortal>
  );
}
